<template>
  <div class="grid-y flex-grow">
    <Loading :loading="loading">
      <div class="cell p-8 message-template-settings">
        <div class="settings-header">
          <div>
            <b class="text-16px leading-18px title-divided">Шаблоны сообщений</b>
            <p class="text-sm text-muted mt-4 mb-0">
              Системные и пользовательские ответы iWALY. Группы свернуты по умолчанию,
              редактирование открывается в большом окне, каждое сохранение создаёт версию.
            </p>
          </div>
          <div class="header-actions">
            <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="fetch">
              Обновить
            </button>
            <button class="btn btn-sm btn-primary" :disabled="!canCreateAny" @click="openCreate()">
              + Новый шаблон
            </button>
          </div>
        </div>

        <div v-if="!storageReady" class="storage-warning mt-6">
          Миграция <b>support_message_templates</b> ещё не применена. Просмотр доступен,
          сохранение отключено.
        </div>
        <div v-else-if="!versioningReady" class="storage-warning mt-6">
          Template Manager V2 ещё не применён на backend. Старое редактирование доступно,
          создание и история версий отключены.
        </div>

        <div class="manager-tools mt-6">
          <input
            v-model.trim="search"
            type="search"
            class="form-control search-input"
            placeholder="Поиск по названию, ключу или тексту">
          <label class="compact-check">
            <input v-model="showArchived" type="checkbox">
            Архивные
          </label>
          <div class="manager-summary">{{ visibleCount }} шабл.</div>
        </div>

        <div class="placeholder-help mt-4">
          <div>
            <b>Переменные</b>
            <span class="text-xs text-muted">— подставляются сервером при использовании.</span>
          </div>
          <div class="placeholder-list mt-2">
            <code v-for="item in supportedPlaceholders" :key="item">{{ item }}</code>
          </div>
        </div>

        <section v-for="group in filteredGroups" :key="group.key" class="template-group mt-4">
          <div class="group-head">
            <button type="button" class="group-toggle" @click="toggleGroup(group.key)">
              <span class="group-chevron">{{ isGroupOpen(group.key) ? '▾' : '▸' }}</span>
              <span class="group-title">{{ group.title }}</span>
              <span class="group-count">{{ group.templates.length }} шабл.</span>
            </button>
            <button
              v-if="group.canCreate"
              type="button"
              class="btn btn-xs btn-outline-primary group-add"
              @click="openCreate(group.key)">
              + Добавить
            </button>
          </div>

          <div v-if="isGroupOpen(group.key)" class="group-body">
            <div v-if="!group.templates.length" class="empty-group">
              По текущему фильтру шаблонов нет.
            </div>

            <article
              v-for="item in group.templates"
              :key="item.key"
              :class="['template-row', { archived: item.isCustom && !item.isActive }]">
              <div class="template-main">
                <div class="template-title-line">
                  <b>{{ item.title || item.key }}</b>
                  <span v-if="item.quickSlot" class="quick-badge">Быстрый #{{ item.quickSlot }}</span>
                  <span v-if="item.overridden" class="override-badge">изменён</span>
                  <span v-if="item.isCustom" class="custom-badge">пользовательский</span>
                  <span v-if="item.isCustom && !item.isActive" class="archive-badge">архив</span>
                </div>
                <code class="template-key">{{ item.key }}</code>
                <div class="template-snippet">{{ compact(item.message) }}</div>
                <div class="template-meta">
                  <span v-if="item.updatedAt">Изменён {{ formatDate(item.updatedAt) }}</span>
                  <span v-if="versioningReady">Версий: {{ item.versionCount || 0 }}</span>
                </div>
              </div>

              <div class="template-actions">
                <button class="btn btn-sm btn-outline-primary" @click="openEditor(item)">
                  Редактировать
                </button>
                <button
                  v-if="versioningReady"
                  class="btn btn-sm btn-outline-secondary"
                  @click="openHistory(item)">
                  История
                </button>
                <button
                  v-if="canDuplicate(item)"
                  class="btn btn-sm btn-outline-secondary"
                  @click="duplicate(item)">
                  Копия
                </button>
                <button
                  v-if="item.isCustom && item.isActive"
                  class="btn btn-sm btn-outline-secondary"
                  @click="archive(item)">
                  В архив
                </button>
                <button
                  v-if="item.isCustom && !item.isActive"
                  class="btn btn-sm btn-outline-primary"
                  @click="activate(item)">
                  Активировать
                </button>
                <button
                  v-if="!item.isCustom && item.overridden"
                  class="btn btn-sm btn-outline-secondary"
                  @click="reset(item)">
                  По умолчанию
                </button>
              </div>
            </article>
          </div>
        </section>
      </div>
    </Loading>

    <div v-if="editorOpen" class="template-modal-backdrop" @click.self="closeEditor">
      <section class="template-modal">
        <header class="modal-head">
          <div>
            <div class="modal-kicker">{{ editorIsNew ? 'Новый шаблон' : 'Редактирование' }}</div>
            <h2>{{ editor.title || (editorIsNew ? 'Без названия' : editor.key) }}</h2>
            <code v-if="!editorIsNew">{{ editor.key }}</code>
          </div>
          <button type="button" class="modal-close" aria-label="Закрыть" @click="closeEditor">×</button>
        </header>

        <div class="modal-grid">
          <main class="editor-pane">
            <label v-if="editorIsNew" class="field-label">
              <span>Группа</span>
              <select v-model="editor.recipientType" class="form-control">
                <option v-for="group in creatableGroups" :key="group.key" :value="group.key">
                  {{ group.title }}
                </option>
              </select>
            </label>

            <label v-if="!isPreset(editor)" class="field-label mt-4">
              <span>Название</span>
              <input v-model="editor.title" class="form-control" maxlength="191">
            </label>

            <div class="editor-toolbar mt-4">
              <span class="field-caption">{{ isPreset(editor) ? 'Текст / значение поля' : 'Текст' }}</span>
              <div v-if="placeholdersAllowed(editor)" class="insert-placeholders">
                <button
                  v-for="placeholder in supportedPlaceholders"
                  :key="placeholder"
                  type="button"
                  class="placeholder-insert"
                  @click="insertPlaceholder(placeholder)">
                  + {{ placeholderLabel(placeholder) }}
                </button>
              </div>
            </div>

            <textarea
              ref="editorTextarea"
              v-model="editor.message"
              class="form-control template-editor"
              maxlength="30000"></textarea>

            <div class="editor-footer-line">
              <span>{{ String(editor.message || '').length }} / 30000</span>
              <span v-if="editor.isCustom && !editor.isActive" class="archive-note">
                Шаблон в архиве и не показывается операторам.
              </span>
            </div>

            <div class="preview-panel mt-4">
              <div class="preview-title">Предпросмотр</div>
              <pre>{{ preview(editor) }}</pre>
            </div>
          </main>

          <aside class="history-pane">
            <div class="history-head">
              <b>История версий</b>
              <button
                v-if="!editorIsNew && versioningReady"
                class="btn btn-xs btn-outline-secondary"
                :disabled="versionsLoading"
                @click="loadVersions(editor.key)">
                Обновить
              </button>
            </div>

            <div v-if="editorIsNew" class="history-empty">
              История появится после первого сохранения.
            </div>
            <div v-else-if="!versioningReady" class="history-empty">
              История появится после миграции V2.
            </div>
            <div v-else-if="versionsLoading" class="history-empty">Загружаем…</div>
            <div v-else-if="!versions.length" class="history-empty">Сохранённых версий пока нет.</div>

            <div v-else class="version-list">
              <details v-for="version in versions" :key="version.version" class="version-card">
                <summary>
                  <span>{{ version.version === 0 ? 'Исходная' : `v${version.version}` }}</span>
                  <small>{{ versionAction(version.action) }}</small>
                </summary>
                <div class="version-meta">
                  <span v-if="version.createdAt">{{ formatDate(version.createdAt) }}</span>
                  <span v-if="version.createdByName">{{ version.createdByName }}</span>
                </div>
                <b class="version-title">{{ version.title }}</b>
                <pre class="version-text">{{ version.message }}</pre>
                <button
                  class="btn btn-xs btn-outline-primary"
                  :disabled="restoringVersion !== null"
                  @click="restoreVersion(version)">
                  {{ restoringVersion === version.version ? 'Восстанавливаем…' : 'Восстановить' }}
                </button>
              </details>
            </div>
          </aside>
        </div>

        <footer class="modal-actions">
          <div>
            <button
              v-if="!editorIsNew && editor.isCustom && editor.isActive"
              class="btn btn-sm btn-outline-secondary"
              @click="archive(editor)">
              В архив
            </button>
          </div>
          <div class="modal-actions-right">
            <button class="btn btn-sm btn-outline-secondary" @click="closeEditor">Закрыть</button>
            <button class="btn btn-sm btn-primary" :disabled="!canSaveEditor" @click="saveEditor">
              {{ saving ? 'Сохраняем…' : 'Сохранить' }}
            </button>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Loading from '@/components/Loading.vue';
import {
  supportMessageTemplates,
  updateSupportMessageTemplate,
  resetSupportMessageTemplate,
} from '@/api/settings';
import {
  createSupportMessageTemplate,
  supportMessageTemplateVersions,
  restoreSupportMessageTemplateVersion,
} from '@/api/settings/supportTemplatesV2';
import { iwalyNotify } from '@/notify';

export default {
  components: { Loading },

  data() {
    return {
      loading: true,
      saving: false,
      groups: [],
      supportedPlaceholders: [],
      canEdit: false,
      storageReady: false,
      versioningReady: false,
      expandedGroups: {},
      search: '',
      showArchived: false,
      editorOpen: false,
      editorIsNew: false,
      editor: {},
      versions: [],
      versionsLoading: false,
      restoringVersion: null,
    };
  },

  computed: {
    creatableGroups() {
      return this.groups.filter((group) => group.canCreate);
    },
    canCreateAny() {
      return this.canEdit
        && this.storageReady
        && this.versioningReady
        && this.creatableGroups.length > 0;
    },
    filteredGroups() {
      const query = this.search.toLocaleLowerCase('ru');
      return this.groups.map((group) => ({
        ...group,
        templates: (group.templates || []).filter((item) => {
          if (!this.showArchived && item.isCustom && !item.isActive) return false;
          if (!query) return true;
          return [item.title, item.key, item.message]
            .filter(Boolean)
            .join(' ')
            .toLocaleLowerCase('ru')
            .includes(query);
        }),
      }));
    },
    visibleCount() {
      return this.filteredGroups.reduce((sum, group) => sum + group.templates.length, 0);
    },
    canSaveEditor() {
      if (!this.canEdit || !this.storageReady || this.saving) return false;
      if (!String(this.editor.title || '').trim()) return false;
      if (!String(this.editor.message || '').trim()) return false;
      if (this.editorIsNew && (!this.versioningReady || !this.editor.recipientType)) return false;
      return true;
    },
  },

  methods: {
    isPreset(item) {
      return String((item && item.recipientType) || '').startsWith('preset_');
    },
    placeholdersAllowed(item) {
      return !['external', 'email'].includes(String((item && item.recipientType) || ''));
    },
    placeholderLabel(value) {
      return {
        '{{salutation_name}}': 'Имя Отчество',
        '{{first_name}}': 'Имя (старый алиас)',
        '{{order_number}}': '№ заказа',
        '{{order_status}}': 'Статус',
        '{{order_amount}}': 'Сумма',
      }[value] || value;
    },
    groupByKey(key) {
      return this.groups.find((group) => group.key === key) || null;
    },
    canDuplicate(item) {
      const group = this.groupByKey(item.recipientType);
      return !!(group && group.canCreate && this.canCreateAny);
    },
    isGroupOpen(key) {
      return !!this.expandedGroups[key];
    },
    toggleGroup(key) {
      this.$set(this.expandedGroups, key, !this.isGroupOpen(key));
    },
    compact(value, limit = 220) {
      const text = String(value || '').replace(/\s+/g, ' ').trim();
      return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
    },
    formatDate(value) {
      const date = moment(value);
      return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '—';
    },
    versionAction(action) {
      return {
        baseline: 'состояние до V2',
        created: 'создан',
        updated: 'изменён',
        override: 'изменён',
        restored: 'восстановлен',
        archived: 'архивирован',
        reset: 'по умолчанию',
        default: 'системная версия',
      }[action] || action || 'версия';
    },
    applyData(data) {
      this.groups = (data.groups || []).map((group) => ({
        ...group,
        templates: (group.templates || []).map((item) => ({ ...item })),
      }));
      this.supportedPlaceholders = data.supportedPlaceholders || [];
      this.canEdit = !!data.canEdit;
      this.storageReady = !!data.storageReady;
      this.versioningReady = !!data.versioningReady;
    },
    async fetch() {
      this.loading = true;
      try {
        const response = await supportMessageTemplates();
        this.applyData(response.data || {});
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось загрузить шаблоны сообщений' });
      } finally {
        this.loading = false;
      }
    },
    openCreate(groupKey = null) {
      if (!this.canCreateAny) return;
      const group = groupKey ? this.groupByKey(groupKey) : this.creatableGroups[0];
      if (!group || !group.canCreate) return;
      this.editorIsNew = true;
      this.editor = {
        recipientType: group.key,
        title: '',
        message: '',
        isCustom: true,
        isActive: true,
      };
      this.versions = [];
      this.editorOpen = true;
    },
    async openEditor(item) {
      this.editorIsNew = false;
      this.editor = JSON.parse(JSON.stringify(item));
      this.editorOpen = true;
      this.versions = [];
      if (this.versioningReady) await this.loadVersions(item.key);
    },
    async openHistory(item) {
      await this.openEditor(item);
    },
    closeEditor() {
      if (this.saving || this.restoringVersion !== null) return;
      this.editorOpen = false;
      this.editorIsNew = false;
      this.editor = {};
      this.versions = [];
    },
    insertPlaceholder(placeholder) {
      if (!this.placeholdersAllowed(this.editor)) return;
      const textarea = this.$refs.editorTextarea;
      const current = String(this.editor.message || '');
      if (!textarea) {
        this.editor.message = `${current}${placeholder}`;
        return;
      }
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || start;
      this.editor.message = current.slice(0, start) + placeholder + current.slice(end);
      this.$nextTick(() => {
        const next = start + placeholder.length;
        textarea.focus();
        textarea.setSelectionRange(next, next);
      });
    },
    async loadVersions(key) {
      if (!key || !this.versioningReady) return;
      this.versionsLoading = true;
      try {
        const response = await supportMessageTemplateVersions(key);
        this.versions = response.data && Array.isArray(response.data.versions)
          ? response.data.versions
          : [];
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось загрузить историю версий' });
      } finally {
        this.versionsLoading = false;
      }
    },
    async saveEditor() {
      if (!this.canSaveEditor) return;
      this.saving = true;
      try {
        if (this.editorIsNew) {
          const response = await createSupportMessageTemplate({
            recipientType: this.editor.recipientType,
            title: String(this.editor.title).trim(),
            message: String(this.editor.message).replace(/\s+$/u, ''),
          });
          const created = response.data || {};
          this.$set(this.expandedGroups, created.recipientType || this.editor.recipientType, true);
          iwalyNotify.success({ text: 'Новый шаблон создан' });
        } else {
          await updateSupportMessageTemplate(this.editor.key, {
            title: String(this.editor.title).trim(),
            message: String(this.editor.message).replace(/\s+$/u, ''),
            isActive: this.editor.isCustom ? !!this.editor.isActive : true,
          });
          iwalyNotify.success({ text: 'Шаблон сохранён · создана новая версия' });
        }
        this.editorOpen = false;
        await this.fetch();
      } catch (error) {
        const data = error && error.response && error.response.data ? error.response.data : {};
        const validation = data.errors && data.errors.message ? data.errors.message[0] : null;
        iwalyNotify.error({ text: validation || data.message || 'Не удалось сохранить шаблон' });
      } finally {
        this.saving = false;
      }
    },
    async duplicate(item) {
      if (!this.canDuplicate(item)) return;
      try {
        const response = await createSupportMessageTemplate({
          recipientType: item.recipientType,
          title: `${item.title || 'Шаблон'} — копия`,
          message: item.message,
        });
        const created = response.data || {};
        this.$set(this.expandedGroups, created.recipientType || item.recipientType, true);
        iwalyNotify.success({ text: 'Создана копия шаблона' });
        await this.fetch();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось создать копию шаблона' });
      }
    },
    async archive(item) {
      if (!item || !item.isCustom || !item.isActive) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Переместить «${item.title}» в архив?`)) return;
      try {
        await resetSupportMessageTemplate(item.key);
        if (this.editorOpen && this.editor.key === item.key) this.editorOpen = false;
        iwalyNotify.success({ text: 'Шаблон перемещён в архив' });
        await this.fetch();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось архивировать шаблон' });
      }
    },
    async activate(item) {
      if (!item || !item.isCustom || item.isActive) return;
      try {
        await updateSupportMessageTemplate(item.key, {
          title: item.title,
          message: item.message,
          isActive: true,
        });
        iwalyNotify.success({ text: 'Шаблон активирован' });
        await this.fetch();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось активировать шаблон' });
      }
    },
    async reset(item) {
      if (!item || item.isCustom || !item.overridden) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Вернуть «${item.title}» к системной версии?`)) return;
      try {
        await resetSupportMessageTemplate(item.key);
        iwalyNotify.success({ text: 'Возвращён системный текст по умолчанию' });
        await this.fetch();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось вернуть шаблон по умолчанию' });
      }
    },
    async restoreVersion(version) {
      if (!this.editor.key || !version || this.restoringVersion !== null) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Восстановить ${version.version === 0 ? 'исходную версию' : `v${version.version}`}?`)) return;
      this.restoringVersion = version.version;
      try {
        const response = await restoreSupportMessageTemplateVersion(this.editor.key, version.version);
        this.editor = { ...this.editor, ...(response.data || {}) };
        iwalyNotify.success({ text: 'Версия восстановлена' });
        await this.loadVersions(this.editor.key);
        await this.fetch();
      } catch (error) {
        iwalyNotify.error({ text: 'Не удалось восстановить версию' });
      } finally {
        this.restoringVersion = null;
      }
    },
    preview(item) {
      const replacements = {
        '{{salutation_name}}': 'Мария Ивановна',
        '{{first_name}}': 'Мария Ивановна',
        '{{order_number}}': '4755',
        '{{order_status}}': 'В работе',
        '{{order_amount}}': '1500',
      };
      return Object.keys(replacements).reduce(
        (text, key) => text.split(key).join(replacements[key]),
        String((item && item.message) || ''),
      );
    },
  },

  mounted() {
    this.fetch();
  },
};
</script>

<style scoped>
.message-template-settings { max-width: 1280px; width: 100%; }
.settings-header { display:flex; justify-content:space-between; align-items:flex-start; gap:18px; }
.header-actions,.template-actions,.modal-actions-right { display:flex; gap:8px; flex-wrap:wrap; }
.storage-warning { border:1px solid #efcf8d; background:#fff9ec; border-radius:8px; padding:12px 14px; font-size:12px; }
.manager-tools { display:flex; align-items:center; gap:12px; }
.search-input { flex:1 1 auto; }
.compact-check { display:flex; align-items:center; gap:6px; margin:0; font-size:12px; white-space:nowrap; }
.manager-summary { color:#7d8794; font-size:11px; white-space:nowrap; }
.placeholder-help { border:1px solid #e1e6ec; border-radius:10px; padding:12px 14px; background:#fafbfc; }
.placeholder-list,.insert-placeholders { display:flex; flex-wrap:wrap; gap:7px; align-items:center; }
.placeholder-list code { background:#eef3f7; border-radius:5px; padding:3px 6px; font-size:11px; }
.template-group { border:1px solid #e1e5ea; border-radius:10px; overflow:hidden; background:#fff; }
.group-head { width:100%; min-height:54px; background:#fafbfc; display:flex; align-items:center; gap:10px; padding:7px 14px; }
.group-head:hover { background:#f6f8fa; }
.group-toggle { flex:1 1 auto; min-width:0; border:0; background:transparent; display:flex; align-items:center; gap:10px; padding:4px 0; text-align:left; cursor:pointer; }
.group-chevron { width:16px; color:#547087; }
.group-title { flex:1 1 auto; font-weight:700; font-size:14px; }
.group-count { color:#8a929e; font-size:11px; }
.group-add { flex:0 0 auto; }
.group-body { border-top:1px solid #edf0f3; }
.empty-group { padding:18px; color:#8a929e; font-size:12px; }
.template-row { display:flex; justify-content:space-between; gap:18px; padding:14px 16px; border-bottom:1px solid #edf0f3; }
.template-row:last-child { border-bottom:0; }
.template-row.archived { opacity:.65; background:#fafafa; }
.template-main { min-width:0; flex:1 1 auto; }
.template-title-line { display:flex; align-items:center; flex-wrap:wrap; gap:7px; }
.template-key { display:block; margin-top:4px; color:#7d8794; font-size:10px; overflow-wrap:anywhere; }
.template-snippet { margin-top:8px; color:#5f6976; font-size:12px; line-height:1.45; }
.template-meta { display:flex; flex-wrap:wrap; gap:12px; margin-top:7px; color:#949ca7; font-size:10px; }
.template-actions { flex:0 0 330px; justify-content:flex-end; align-content:flex-start; }
.quick-badge,.override-badge,.custom-badge,.archive-badge { border-radius:12px; padding:2px 7px; font-size:9px; font-weight:700; }
.quick-badge { background:#eef5ff; color:#3d648c; }
.override-badge { background:#eaf7ef; color:#2f7650; }
.custom-badge { background:#f1edff; color:#62508e; }
.archive-badge { background:#f0f1f2; color:#727b86; }
.template-modal-backdrop { position:fixed; inset:0; z-index:2000; background:rgba(25,31,39,.52); display:flex; align-items:center; justify-content:center; padding:24px; }
.template-modal {
  width:min(1480px,98vw);
  height:min(920px,96vh);
  background:#fff;
  border-radius:12px;
  box-shadow:0 24px 70px rgba(24,32,44,.28);
  display:flex;
  flex-direction:column;
  overflow:hidden;
}
.modal-head { flex:0 0 auto; display:flex; justify-content:space-between; gap:18px; padding:18px 22px; border-bottom:1px solid #e8ebef; }
.modal-head h2 { margin:3px 0; font-size:19px; }
.modal-head code { color:#7d8794; font-size:10px; }
.modal-kicker { color:#3b765e; font-size:10px; font-weight:700; text-transform:uppercase; }
.modal-close { width:36px; height:36px; border:0; border-radius:8px; background:#f2f4f6; font-size:24px; line-height:1; cursor:pointer; }
.modal-grid { flex:1 1 auto; min-height:0; display:grid; grid-template-columns:minmax(0,3.2fr) minmax(320px,1fr); }
.editor-pane,.history-pane { min-height:0; overflow-y:auto; padding:20px 22px; }
.history-pane { border-left:1px solid #e8ebef; background:#fafbfc; }
.field-label { display:block; }
.field-label > span,.field-caption { display:block; margin-bottom:5px; font-size:11px; font-weight:700; }
.editor-toolbar { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; }
.insert-placeholders { justify-content:flex-end; }
.placeholder-insert { border:1px solid #d5dde5; border-radius:999px; background:#fff; padding:4px 8px; color:#4e6172; font-size:9px; cursor:pointer; }
.placeholder-insert:hover { border-color:#8fbca8; background:#f5fbf8; }
.template-editor {
  width:100%;
  min-height:560px;
  margin-top:7px;
  resize:vertical;
  box-sizing:border-box;
  font-family:inherit;
  font-size:14px;
  white-space:pre-wrap;
  line-height:1.55;
}
.editor-footer-line { display:flex; justify-content:space-between; gap:12px; margin-top:6px; color:#8a929e; font-size:10px; }
.archive-note { color:#9a6c24; }
.preview-panel { border:1px solid #e1e6ec; border-radius:10px; background:#f8fafb; padding:12px; }
.preview-title { margin-bottom:7px; color:#3b765e; font-size:10px; font-weight:700; }
.preview-panel pre { margin:0; white-space:pre-wrap; word-break:break-word; font-family:inherit; font-size:12px; line-height:1.5; }
.history-head { display:flex; justify-content:space-between; gap:10px; align-items:center; margin-bottom:12px; }
.history-empty { padding:20px 0; color:#8a929e; font-size:12px; }
.version-list { display:flex; flex-direction:column; gap:8px; }
.version-card { border:1px solid #e0e5ea; border-radius:9px; background:#fff; padding:9px 10px; }
.version-card summary { cursor:pointer; display:flex; justify-content:space-between; gap:8px; font-size:11px; font-weight:700; }
.version-card summary small { color:#8a929e; font-weight:500; }
.version-meta { display:flex; flex-wrap:wrap; gap:8px; margin:8px 0 5px; color:#8a929e; font-size:9px; }
.version-title { display:block; font-size:11px; }
.version-text {
  max-height:200px;
  overflow:auto;
  margin:8px 0;
  padding:8px;
  border-radius:6px;
  background:#f7f8fa;
  white-space:pre-wrap;
  word-break:break-word;
  font-family:inherit;
  font-size:10px;
  line-height:1.4;
}
.modal-actions { flex:0 0 auto; min-height:62px; border-top:1px solid #e8ebef; display:flex; justify-content:space-between; align-items:center; gap:12px; padding:12px 22px; }
@media (max-width:900px) {
  .message-template-settings { padding:16px !important; }
  .settings-header,.manager-tools,.template-row { flex-direction:column; align-items:stretch; }
  .template-actions { flex:0 0 auto; justify-content:flex-start; }
  .template-modal-backdrop { padding:0; }
  .template-modal { width:100vw; height:100vh; max-height:none; border-radius:0; }
  .modal-grid { grid-template-columns:1fr; overflow-y:auto; }
  .editor-pane,.history-pane { overflow:visible; }
  .history-pane { border-left:0; border-top:1px solid #e8ebef; }
  .template-editor { min-height:330px; }
}
</style>
